import type { StoryCtx } from './ctx.js';
import { appearanceMatrix, povOf, worldOrder, type AppearanceLevel } from './narrative.js';
import { scenesOf } from './scene.js';

// ---------------------------------------------------------------------------
// Spuren (PF 12, PLAN.md §20): ein Storyline-Diagramm entlang der Weltzeit – wer ist wann wo, wo laufen Stränge parallel, wo treffen sie
// sich. Alles kommt aus dem, was ohnehin da ist (Verweise, Bezüge und Elemente der Szenen, Perspektive); zuordnen muss niemand etwas.
//  - Figuren: je Figur die Szenen, in denen sie auftritt (Perspektive, spricht, anwesend – wie in der Auftrittsmatrix; bloß erwähnt
//    zählt nicht). Treffen sich mehrere Figuren in einer Szene, ist das ein Treffpunkt.
//  - Perspektiven: je Perspektivfigur ihre Szenen – die Erzählstränge; dazu ein Strang für Szenen ohne Perspektive.
// ---------------------------------------------------------------------------

export type TrackMode = 'figures' | 'pov';

export interface TrackStop {
  sceneId: string;
  /** stärkste Art des Auftritts (Perspektive vor spricht vor anwesend); im Perspektiv-Modus immer `pov` */
  level: AppearanceLevel;
}

export interface Track {
  /** Figur bzw. Perspektivfigur; null: Szenen ohne Perspektive */
  entityId: string | null;
  /** Szenen der Spur in der Reihenfolge der Weltzeit */
  stops: TrackStop[];
}

export interface StoryTracks {
  tracks: Track[];
  /** Figuren-Modus: Szenen, in denen sich mindestens zwei Spuren treffen, mit den Figuren darin */
  meetings: Map<string, string[]>;
}

/**
 * Spuren der Geschichte in der Reihenfolge der Weltzeit, nach erstem Auftritt geordnet. `include` beschränkt die Figuren (z. B. auf
 * Personen).
 */
export function storyTracks(ctx: StoryCtx, mode: TrackMode, include?: (entityId: string) => boolean): StoryTracks {
  if (mode === 'pov') {
    const byPov = new Map<string | null, TrackStop[]>();
    for (const scene of worldOrder(ctx, scenesOf(ctx))) {
      const pov = povOf(ctx, scene);
      const key = pov && (!include || include(pov)) ? pov : null;
      byPov.set(key, [...(byPov.get(key) ?? []), { sceneId: scene.id, level: 'pov' }]);
    }
    // die Stränge in der Reihenfolge ihres Beginns, die Szenen ohne Perspektive zuletzt
    const tracks = [...byPov].map(([entityId, stops]) => ({ entityId, stops })).sort((a, b) => Number(a.entityId === null) - Number(b.entityId === null));
    return { tracks, meetings: new Map() };
  }
  const matrix = appearanceMatrix(ctx, 'world', include);
  const tracks: Track[] = matrix.rows
    .map((row) => ({
      entityId: row.entityId,
      stops: row.cells.flatMap((cell, i): TrackStop[] => (cell.length > 0 && cell[0] !== 'mentioned' ? [{ sceneId: matrix.scenes[i]!.id, level: cell[0]! }] : [])),
    }))
    .filter((t) => t.stops.length > 0);
  const present = new Map<string, string[]>();
  for (const t of tracks) for (const s of t.stops) present.set(s.sceneId, [...(present.get(s.sceneId) ?? []), t.entityId!]);
  return { tracks, meetings: new Map([...present].filter(([, ids]) => ids.length > 1)) };
}
